import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Printer,
  Ruler,
  Lightbulb,
  Wrench,
  Gauge,
  Image as ImageIcon,
  UserFocus,
  CheckCircle,
  ShoppingBag,
  Broadcast,
  Package,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

import { container, itemAnim, PHOTOS } from "./studio/constants";
import { CONTENT } from "./studio/content";
import {
  GEAR_PRIORITY_EN,
  GEAR_PRIORITY_ES,
  computeBudgetPlan,
} from "./studio/gear";
import {
  BudgetCalculator,
  BudgetPrintBlock,
} from "./studio/BudgetCalculator";
import { StudioDiagram } from "./studio/StudioDiagram";
import {
  Section,
  BulletList,
  CardGrid,
  ChecklistCard,
  PrintSection,
  PrintCard,
} from "./studio/helpers";

export default function StudioPage() {
  const { lang } = useTranslation();
  const { user } = useAuth();
  const c = CONTENT[lang] || CONTENT.en;
  const [budget, setBudget] = useState(1500);
  const [ownedIds, setOwnedIds] = useState(() => new Set());
  const printRef = useRef(null);

  const isAuthenticated = user && user !== false;

  // Load shopping list on mount / when user logs in
  useEffect(() => {
    if (!isAuthenticated) {
      setOwnedIds(new Set());
      return;
    }
    axios
      .get("/api/shopping-list", { withCredentials: true })
      .then((res) => {
        const ids = Array.isArray(res.data?.owned_item_ids) ? res.data.owned_item_ids : [];
        setOwnedIds(new Set(ids));
      })
      .catch(() => {
        setOwnedIds(new Set());
      });
  }, [isAuthenticated]);

  // Persist shopping list (debounced implicitly because state updates batch)
  const persistOwned = useCallback(
    (nextIds) => {
      if (!isAuthenticated) return;
      axios
        .post(
          "/api/shopping-list",
          { owned_item_ids: Array.from(nextIds) },
          { withCredentials: true }
        )
        .catch(() => {});
    },
    [isAuthenticated]
  );

  const handleToggleOwn = useCallback(
    (id) => {
      setOwnedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        persistOwned(next);
        return next;
      });
    },
    [persistOwned]
  );

  const gearItems = lang === "es" ? GEAR_PRIORITY_ES : GEAR_PRIORITY_EN;
  const plan = computeBudgetPlan(budget, gearItems, ownedIds);
  const tierLabel = c.budget.tiers[plan.tier];

  const ownedSignature = Array.from(ownedIds).sort().join(",");
  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `studio-setup-guide-${lang}.pdf`,
    [lang, budget, ownedSignature]
  );

  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black"
      data-testid="studio-page"
    >
      {/* Screen (dark) UI */}
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          {/* Header */}
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="studio-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* Hero photo */}
          <motion.div variants={itemAnim} className="relative h-52 sm:h-64 rounded-2xl overflow-hidden border border-white/10">
            <img src={PHOTOS.hero} alt="studio" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
          </motion.div>

          {/* Budget Tier Calculator */}
          <motion.div variants={itemAnim}>
            <BudgetCalculator
              lang={lang}
              c={c}
              budget={budget}
              setBudget={setBudget}
              plan={plan}
              ownedIds={ownedIds}
              onToggleOwn={handleToggleOwn}
              isAuthenticated={isAuthenticated}
              totalItems={gearItems.length}
            />
          </motion.div>

          {/* 1. Space */}
          <Section icon={<Ruler weight="fill" className="w-5 h-5 text-[#F5A524]" />} title={c.section1.title} intro={c.section1.intro}>
            <BulletList items={c.section1.points} />
          </Section>

          {/* 2. Lighting */}
          <Section icon={<Lightbulb weight="fill" className="w-5 h-5 text-[#F5A524]" />} title={c.section2.title} intro={c.section2.intro}>
            <CardGrid cards={c.section2.cards} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              {c.section2.roles.map((r, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
                  <div className="text-[#F5A524] text-xs uppercase tracking-widest mb-1 font-semibold">{r.label}</div>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">{r.text}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* 3. Triggers */}
          <Section icon={<Broadcast weight="fill" className="w-5 h-5 text-[#F5A524]" />} title={c.section3.title} intro={c.section3.intro}>
            <BulletList items={c.section3.items} />
          </Section>

          {/* 4. Light meters */}
          <Section icon={<Gauge weight="fill" className="w-5 h-5 text-[#F5A524]" />} title={c.section4.title} intro={c.section4.intro}>
            <BulletList items={c.section4.items} />
          </Section>

          {/* 5. Modifiers */}
          <Section icon={<Wrench weight="fill" className="w-5 h-5 text-[#F5A524]" />} title={c.section5.title} intro={c.section5.intro}>
            <CardGrid cards={c.section5.cards} />
          </Section>

          {/* 6. Backgrounds */}
          <Section icon={<ImageIcon weight="fill" className="w-5 h-5 text-[#F5A524]" />} title={c.section6.title} intro={c.section6.intro}>
            <CardGrid cards={c.section6.cards} />
            <div className="mt-4 bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
              <div className="text-[#F5A524] text-xs uppercase tracking-widest mb-2 font-semibold">
                {lang === "es" ? "Consejos de Pantalla Verde" : "Green Screen Tips"}
              </div>
              <BulletList items={c.section6.greenscreen_tips} />
            </div>
          </Section>

          {/* 7. Support */}
          <Section icon={<UserFocus weight="fill" className="w-5 h-5 text-[#F5A524]" />} title={c.section7.title} intro={c.section7.intro}>
            <BulletList items={c.section7.items} />
          </Section>

          {/* 8. Layouts (SVG diagrams) */}
          <Section icon={<Package weight="fill" className="w-5 h-5 text-[#F5A524]" />} title={`8. ${c.sections[7]}`} intro="">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
                <StudioDiagram variant="small" legend={c.diagram.legend} title={c.diagram.small_title} dark />
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
                <StudioDiagram variant="mid" legend={c.diagram.legend} title={c.diagram.mid_title} dark />
              </div>
            </div>
          </Section>

          {/* 9. Checklists */}
          <Section icon={<CheckCircle weight="fill" className="w-5 h-5 text-[#F5A524]" />} title={c.section9.title} intro="">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ChecklistCard title={c.section9.small.title} items={c.section9.small.items} />
              <ChecklistCard title={c.section9.mid.title} items={c.section9.mid.items} highlight />
            </div>
          </Section>

          <motion.p variants={itemAnim} className="text-[#A1A1AA] text-sm italic text-center max-w-3xl mx-auto leading-relaxed">
            "{c.footer_note}"
          </motion.p>
        </motion.div>
      </div>

      {/* Print / PDF (white) */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="studio-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>

        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">{c.toc}</h2>
          <div className="grid grid-cols-2 gap-x-6 text-sm">
            {c.sections.map((s, i) => (
              <div key={i} className="text-gray-700">
                <span className="text-gray-400 mr-1">{i + 1}.</span>
                {s}
              </div>
            ))}
          </div>
        </div>

        <BudgetPrintBlock budget={budget} tierLabel={tierLabel} plan={plan} c={c} />

        <PrintSection title={c.section1.title} intro={c.section1.intro} items={c.section1.points} />

        <PrintSection title={c.section2.title} intro={c.section2.intro}>
          {c.section2.cards.map((card, i) => (
            <PrintCard key={i} card={card} />
          ))}
          <div className="mt-3 grid grid-cols-1 gap-2">
            {c.section2.roles.map((r, i) => (
              <p key={i} className="text-sm">
                <strong>{r.label}:</strong> {r.text}
              </p>
            ))}
          </div>
        </PrintSection>

        <PrintSection title={c.section3.title} intro={c.section3.intro} items={c.section3.items} />
        <PrintSection title={c.section4.title} intro={c.section4.intro} items={c.section4.items} />

        <PrintSection title={c.section5.title} intro={c.section5.intro}>
          {c.section5.cards.map((card, i) => (
            <PrintCard key={i} card={card} />
          ))}
        </PrintSection>

        <PrintSection title={c.section6.title} intro={c.section6.intro}>
          {c.section6.cards.map((card, i) => (
            <PrintCard key={i} card={card} />
          ))}
          <div className="mt-3">
            <p className="text-sm font-semibold">
              {lang === "es" ? "Consejos de Pantalla Verde" : "Green Screen Tips"}:
            </p>
            <ul className="list-disc ml-5 mt-1">
              {c.section6.greenscreen_tips.map((tip, i) => (
                <li key={i} className="text-xs text-gray-700 leading-relaxed mb-1">
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </PrintSection>

        <PrintSection title={c.section7.title} intro={c.section7.intro} items={c.section7.items} />

        {/* 8. Layouts — print SVGs */}
        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-3 border-b-2 border-gray-200 pb-2">
            8. {c.sections[7]}
          </h2>
          <div className="grid grid-cols-1 gap-6">
            <StudioDiagram variant="small" legend={c.diagram.legend} title={c.diagram.small_title} />
            <StudioDiagram variant="mid" legend={c.diagram.legend} title={c.diagram.mid_title} />
          </div>
        </div>

        {/* 9. Checklists */}
        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-3 border-b-2 border-gray-200 pb-2">
            {c.section9.title}
          </h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-base font-bold mb-2 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4" />
                {c.section9.small.title}
              </h3>
              <ul className="list-disc ml-5 space-y-1">
                {c.section9.small.items.map((it, i) => (
                  <li key={i} className="text-xs text-gray-700">{it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-base font-bold mb-2 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-amber-600" />
                {c.section9.mid.title}
              </h3>
              <ul className="list-disc ml-5 space-y-1">
                {c.section9.mid.items.map((it, i) => (
                  <li key={i} className="text-xs text-gray-700">{it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-600 italic text-center max-w-2xl mx-auto leading-relaxed mt-8">
          "{c.footer_note}"
        </p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
